import React from 'react';
import { Card, CardHeader, CardMedia, CardContent, Typography, Button, Grid, Box, Tooltip } from '@mui/material';
import { getEventCardDescriptionText, getEventCardHoverText } from '../../utils/eventText';
import { formatDate, getEventStatus, formatAgeRange } from '../../utils/index.tsx';
import { EventsCardProps, RegistrationStatus } from '../../types.ts';
import { EVENT_CARD_WIDTH, fixedAspectRatioImageSx } from '../../constants/layout';
import familyIcon from '../../assets/family.png';

const EventsCard: React.FC<EventsCardProps> = ({ event, onRegisterClick, selectedChildId }) => {
    const { status, className } = getEventStatus(event, selectedChildId);
    // IMPORTANT: Keep original conditions intact, but treat "Registered" as "Open"
    // for CTA/click behavior so we don't show "Closed" for enrolled children.
    const effectiveStatus =
        status === RegistrationStatus.REGISTERED ? RegistrationStatus.OPEN : status;
    const effectiveClassName = status === RegistrationStatus.REGISTERED ? 'open' : className;

    const isClosedWithoutWaitlist = effectiveStatus !== 'Open' && !event.isWaitlistEnabled;
    const handleCardClick = (e: React.MouseEvent) => {
        // Only navigate if click is not on a button and event is not closed without waitlist
        const target = e.target as HTMLElement;
        if (target.closest('button') || isClosedWithoutWaitlist) {
            return;
        }
        onRegisterClick(event);
    };

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                width: EVENT_CARD_WIDTH,
                minWidth: EVENT_CARD_WIDTH,
                maxWidth: EVENT_CARD_WIDTH,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 8,
                cursor: isClosedWithoutWaitlist ? 'default' : 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': isClosedWithoutWaitlist
                    ? {}
                    : {
                        transform: 'translateY(-4px)',
                        boxShadow: 12,
                    },
            }}
            className="events-card"
            elevation={3}
        >
            <CardMedia
                component="div"
                onClick={handleCardClick}
                sx={{
                    width: '100%',
                    ...fixedAspectRatioImageSx,
                    backgroundImage: event.signedUrl ? `url(${event.signedUrl})` : 'none',
                    backgroundColor: event.signedUrl ? 'transparent' : 'grey.200',
                    cursor: isClosedWithoutWaitlist ? 'default' : 'pointer',
                }}
            />

            <CardHeader
                onClick={handleCardClick}
                title={event.name}
                subheader={`${formatDate(event.startDate)} - ${formatDate(event.endDate)}`}
                sx={{
                    pb: 1,
                    flexShrink: 0,
                    cursor: isClosedWithoutWaitlist ? 'default' : 'pointer',
                    '& .MuiCardHeader-title': { fontSize: '1rem' },
                    '& .MuiCardHeader-subheader': { fontSize: '0.75rem' },
                }}
            />

            <CardContent sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                paddingTop: '8px !important',
                paddingBottom: '12px !important',
                // overflow visible so content is never silently clipped at the
                // CardContent boundary; individual sections own their overflow.
                overflow: 'visible',
                cursor: isClosedWithoutWaitlist ? 'default' : 'pointer',
            }}>
                {/* Age Range */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexShrink: 0 }}>
                    <img src={familyIcon} alt="Age Range" style={{ width: 16, height: 16, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem' }}>
                        Age Range
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {formatAgeRange(event.minMonthsRestrictions ?? 0, event.maxMonthsRestrictions)}
                    </Typography>
                </Box>

                <Tooltip
                    title={getEventCardHoverText(event)}
                    placement="top"
                    enterDelay={400}
                    slotProps={{
                        tooltip: {
                            sx: {
                                maxWidth: 360,
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.75rem',
                            },
                        },
                    }}
                >
                    {/*
                     * Outer Box: flex child that grows but never collapses below
                     * the minimum needed to show ~120 characters (≥3 lines at
                     * 0.8rem / lineHeight 1.35 = 3 × 0.8 × 1.35 × 16px ≈ 51.8px).
                     * Using 3.5rem gives comfortable padding above that minimum.
                     */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            flexShrink: 1,
                            mb: 1,
                            minHeight: '3.5rem',
                            overflow: 'hidden',
                        }}
                    >
                        {/*
                         * Inner Typography: line-clamp lives here on the actual
                         * text node so the flex-child Box keeps its block display
                         * for the parent flex algorithm.
                         */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.8rem',
                                lineHeight: 1.35,
                                color: 'text.secondary',
                                display: '-webkit-box',
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {getEventCardDescriptionText(event)}
                        </Typography>
                    </Box>
                </Tooltip>

                <Box sx={{ mt: 'auto', px: 1, flexShrink: 0 }}>
                    <Grid container spacing={1} alignItems="center" justifyContent="center" mt={0.5}>
                        <Grid item xs={8} sx={{ display: 'flex', justifyContent: 'center' }}>
                            {effectiveStatus === 'Open' && event.totalOccupiedSeats !== event.capacity && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="small"
                                    className={effectiveClassName}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRegisterClick(event);
                                    }}
                                    sx={{ fontSize: '0.75rem', py: 0.5 }}
                                >
                                    Register
                                </Button>
                            )}
                            {(effectiveStatus !== 'Open' || event.totalOccupiedSeats >= event.capacity) && !event.isWaitlistEnabled && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="small"
                                    className={effectiveClassName}
                                    disabled
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ fontSize: '0.75rem', py: 0.5 }}
                                >
                                    Closed
                                </Button>
                            )}
                            {event.totalOccupiedSeats >= event.capacity && !!event.isWaitlistEnabled && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="small"
                                    className={"open"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRegisterClick(event);
                                    }}
                                    sx={{ fontSize: '0.75rem', py: 0.5 }}
                                >
                                    Waitlist
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" className="spots-left" sx={{ fontSize: '0.7rem' }}>
                                {event.totalOccupiedSeats === event.capacity ? 'No Spots' :
                                    event.capacity - event.totalOccupiedSeats === 1 ? '1 Spot' :
                                        `${event.capacity - event.totalOccupiedSeats} Spots`}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </CardContent>
        </Card>
    );
};

export default EventsCard;
